import { createContext, useContext, useMemo, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getServices, addServices, updateServices } from "../../api/services";
import useToaster from "../../components/alert";
import Const from "../../utils/constant";

const PageContext = createContext();

export const PageProvider = ({ children }) => {
  const serviceListArray = useRef();
  const ourValueArray = useRef();
  const awardsAndRecognitionListArray = useRef();
  const toaster = useToaster();

  const getServicesQuery = useQuery({
    queryKey: ["services"],
    queryFn: getServices,
  });
  const addServiceQuery = useMutation({ mutationFn: addServices });
  const updateSeviceQuery = useMutation({ mutationFn: updateServices });

  const initialValues = useMemo(() => {
    const serviceData = getServicesQuery?.data?.data?.[0] || {};

    return {
      meta: {
        meta_title: serviceData?.meta_title || "",
        meta_slug_url: serviceData?.meta_slug_url || "",
        meta_description: serviceData?.meta_description || "",
        keywords: serviceData?.keywords || "",
      },
      banner: {
        heading: serviceData?.heading || "",
        description: serviceData?.description || "",
        cta: serviceData?.cta_label || "",
        cta_redirect_link: serviceData?.cta_redirect_link || "",
        banner_video: serviceData?.banner_video || "",
        cto_label: serviceData?.cto_label || "",
      },
      services: {
        heading: serviceData?.service_heading || "",
        subheading: serviceData?.service_sub_heading || "",
        image: serviceData?.service_sub_banner || "",
        servicesList: serviceData?.services?.map((item) => ({
          heading: item.title || "",
          description: item.description || "",
          _id: item._id || "",
        })),
      },
      ourValue: {
        heading: serviceData?.values_heading || "",
        subheading: serviceData?.values_sub_heading || "",
        ourValueTable: serviceData?.values_list?.map((item) => ({
          heading: item.heading || "",
          subheading: item.sub_heading || "",
          logo: item.logo || "",
        })),
      },
      awardsAndRecognition: {
        heading: serviceData?.recognize_heading || "",
        subheading: serviceData?.recognize_sub_heading || "",
        awardsAndRecognitionList: serviceData?.recognize_list?.map((item) => ({
          heading: item.heading || "",
          subheading: item.sub_heading || "",
          logo: item.logo || "",
        })),
      },
      catalog_download: {
        title: serviceData?.catalog_download?.title || "",
        file: serviceData?.catalog_download?.file || "",
      },

      brochure_download: {
        title: serviceData?.brochure_download?.title || "",
        file: serviceData?.brochure_download?.file || "",
      },
    };
  }, [getServicesQuery]);

  const handleSubmit = async (values) => {
    const payload = {
      meta_title: values?.meta?.meta_title || "",
      meta_slug_url: values?.meta?.meta_slug_url || "",
      meta_description: values?.meta?.meta_description || "",
      keywords: values?.meta?.keywords || "",

      heading: values?.banner?.heading || "",
      description: values?.banner?.description || "",
      cta_label: values?.banner?.cta || "",
      cta_redirect_link: values?.banner?.cta_redirect_link || "",
      banner_video: values?.banner?.banner_video || "",
      cto_label: values?.banner?.cto_label || "",

      service_heading: values?.services?.heading || "",
      service_sub_heading: values?.services?.subheading || "",
      service_sub_banner: values?.services?.image || "",
      services: values?.services?.servicesList?.map((item) => ({
        title: item.heading || "",
        description: item.description || "",
        _id: item._id || "",
      })),

      values_heading: values?.ourValue?.heading || "",
      values_sub_heading: values?.ourValue?.subheading || "",
      values_list: values?.ourValue?.ourValueTable?.map((item) => ({
        heading: item.heading || "",
        sub_heading: item.subheading || "",
        logo: item.logo || "",
      })),

      recognize_heading: values?.awardsAndRecognition?.heading || "",
      recognize_sub_heading: values?.awardsAndRecognition?.subheading || "",
      recognize_list:
        values?.awardsAndRecognition?.awardsAndRecognitionList?.map((item) => ({
          heading: item.heading || "",
          sub_heading: item.subheading || "",
          logo: item.logo || "",
        })),

      brochure_download: {
        title: values?.brochure_download?.title || "",
        file: values?.brochure_download?.file || "",
      },

      catalog_download: {
        title: values?.catalog_download?.title || "",
        file: values?.catalog_download?.file || "",
      },
    };

    const serviceData = getServicesQuery?.data?.data?.[0] || {};

    if (serviceData._id) {
      payload.id = serviceData._id;
      updateSeviceQuery.mutate(payload, {
        onSuccess: () => {
          toaster.success(
            Const.MESSAGES.SUCCESS,
            Const.MESSAGES.DATA_IS_UPDATED_SUCCESSFULLY
          );
        },
      });
      return;
    }
    addServiceQuery.mutate(payload, {
      onSuccess: () => {
        toaster.success(
          Const.MESSAGES.SUCCESS,
          Const.MESSAGES.DATA_IS_UPDATED_SUCCESSFULLY
        );
      },
    });
  };

  const handleServiceDelete = (index) => () => {
    serviceListArray.current.remove(index);
  };

  const handleServiceAdd = () => {
    serviceListArray.current.push({ heading: "", description: "" });
  };

  const handleValuesDelete = (index) => () => {
    ourValueArray.current.remove(index);
  };

  const handleValuesAdd = () => {
    ourValueArray.current.push({ heading: "", description: "", logo: "" });
  };

  const handleAwardDelete = (index) => () => {
    awardsAndRecognitionListArray.current.remove(index);
  };

  const handleAwardsAdd = () => {
    awardsAndRecognitionListArray.current.push({
      heading: "",
      description: "",
      logo: "",
    });
  };

  return (
    <PageContext.Provider
      value={{
        initialValues,
        handleSubmit,
        serviceListArray,
        handleServiceDelete,
        handleServiceAdd,
        ourValueArray,
        handleValuesDelete,
        handleValuesAdd,
        awardsAndRecognitionListArray,
        handleAwardDelete,
        handleAwardsAdd,
        getServicesQuery,
      }}>
      {children}
    </PageContext.Provider>
  );
};

export const usePageProvider = () => {
  return useContext(PageContext);
};
